import React from "react"
import { AwardManagerEntity } from "../../Objects/Awards/AwardManagerEntity"
import { useTranslation } from "react-i18next"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"

interface AwardProgressCardProps {
  awardManager: AwardManagerEntity
  className?: string
}

export const AwardProgressCard: React.FC<AwardProgressCardProps> = ({ awardManager, className = "" }) => {
  const { t } = useTranslation()

  if (
    !awardManager?.awardEntity ||
    awardManager.status !== EEnableStatusType.Active ||
    awardManager.awardEntity.status !== EEnableStatusType.Active
  ) {
    return null
  }

  const { currentCount, timeWon, hasAwardToTake } = awardManager
  const { countToWin, minimumValue, name } = awardManager.awardEntity

  const progress = Math.min((currentCount / countToWin) * 100, 100)
  const remaining = Math.max(countToWin - currentCount, 0)

  return (
    <div
      className={`relative overflow-hidden rounded-[0.5rem] p-5 border-4 shadow-md transition-all duration-300 ${
        hasAwardToTake
          ? "bg-gradient-to-br from-emerald-20 to-green-100 border-emerald-200"
          : "bg-gradient-to-br from-amber-20 to-orange-100 border-amber-200"
      } ${className}`}
    >
      <div className="absolute top-0 right-0 opacity-5 text-[125px] select-none">🏆</div>

      <div className="relative z-10 space-y-5">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-xs uppercase tracking-widest font-black text-gray-500">
              {t("publicMenu:awards.loyalty_program")}
            </div>

            <div className="text-xl font-black text-gray-900">{name}</div>

            <div className="text-xs text-gray-500 mt-1">
              {t("publicMenu:awards.minimum_order", { value: minimumValue.toFixed(2) })}
            </div>
          </div>

          <div className="text-right">
            <div className="text-2xl font-black text-gray-900">
              {currentCount}/{countToWin}
            </div>

            <div className="text-[10px] uppercase font-black tracking-widest text-gray-400">
              {t("publicMenu:awards.current_cycle")}
            </div>
          </div>
        </div>

        <div className="w-full h-3 bg-white/75 ring-1 ring-orange-400/60 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-700 ${
              hasAwardToTake
                ? "bg-gradient-to-r from-emerald-400 to-green-500"
                : "bg-gradient-to-r from-amber-400 to-orange-500"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {hasAwardToTake ? (
          <div className="text-emerald-700 font-black text-sm">{t("publicMenu:awards.ready_to_claim")}</div>
        ) : (
          <div className="text-sm text-gray-700 font-semibold">
            {t("publicMenu:awards.remaining_orders", { count: remaining })}
          </div>
        )}

        <div className="pt-4 border-t border-white/50 text-xs text-gray-500 flex justify-between">
          <span className="font-black">{t("publicMenu:awards.claimed_times", { count: timeWon })}</span>

          <span className="font-black">{t("publicMenu:awards.goal_orders", { count: countToWin })}</span>
        </div>
      </div>
    </div>
  )
}
